import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, Button, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __ } from "@wordpress/i18n";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Highlight", "content-egg"), value: "highlight" },
    { label: __("Inline", "content-egg"), value: "inline" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, pros, cons, quick_take, color_scheme } = attributes;

    const addItem = (key) => {
        setAttributes({ [key]: [...(attributes[key] || []), ""] });
    };

    const updateItem = (key, index, value) => {
        const updated = [...(attributes[key] || [])];
        updated[index] = value;
        setAttributes({ [key]: updated });
    };

    const removeItem = (key, index) => {
        const updated = [...(attributes[key] || [])];
        updated.splice(index, 1);
        setAttributes({ [key]: updated });
    };

    const renderList = (key, label) => (
        <PanelBody title={label} initialOpen={true}>
            {(attributes[key] || []).map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextControl
                            value={item}
                            onChange={(val) => updateItem(key, i, val)}
                            __nextHasNoMarginBottom
                        />
                    </div>
                    <Button
                        icon="no-alt"
                        isDestructive
                        size="small"
                        onClick={() => removeItem(key, i)}
                        label={__("Remove", "content-egg")}
                        style={{ flexShrink: 0, marginTop: 0 }}
                    />
                </div>
            ))}
            <Button variant="secondary" size="small" onClick={() => addItem(key)} style={{ marginTop: "4px" }}>
                {__("Add item", "content-egg")}
            </Button>
        </PanelBody>
    );

    const hasData = pros.length > 0 || cons.length > 0 || (attributes.best_for || []).length > 0 || (attributes.not_for || []).length > 0;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>
            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(val) => setAttributes({ variant: val })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={[
                            { label: __("Auto (site default)", "content-egg"), value: "auto" },
                            { label: __("Light", "content-egg"), value: "light" },
                            { label: __("Dark", "content-egg"), value: "dark" },
                        ]}
                        onChange={(val) => setAttributes({ color_scheme: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
                {renderList("pros", __("Pros", "content-egg"))}
                {renderList("cons", __("Cons", "content-egg"))}
                {renderList("best_for", __("Best for", "content-egg"))}
                {renderList("not_for", __("Not for", "content-egg"))}
                {variant === "highlight" && (
                    <PanelBody title={__("Quick Take", "content-egg")}>
                        <TextControl
                            value={quick_take}
                            onChange={(val) => setAttributes({ quick_take: val })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}
                <PanelBody title={__("Labels", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Pros label", "content-egg")}
                        value={attributes.label_pros}
                        placeholder={__("Pros", "content-egg")}
                        onChange={(val) => setAttributes({ label_pros: val })}
                        __nextHasNoMarginBottom
                    />
                    <TextControl
                        label={__("Cons label", "content-egg")}
                        value={attributes.label_cons}
                        placeholder={__("Cons", "content-egg")}
                        onChange={(val) => setAttributes({ label_cons: val })}
                        __nextHasNoMarginBottom
                    />
                    <TextControl
                        label={__("Best for label", "content-egg")}
                        value={attributes.label_best_for}
                        placeholder={__("Best for", "content-egg")}
                        onChange={(val) => setAttributes({ label_best_for: val })}
                        __nextHasNoMarginBottom
                    />
                    <TextControl
                        label={__("Not for label", "content-egg")}
                        value={attributes.label_not_for}
                        placeholder={__("Not for", "content-egg")}
                        onChange={(val) => setAttributes({ label_not_for: val })}
                        __nextHasNoMarginBottom
                    />
                    {variant === "highlight" && (
                        <TextControl
                            label={__("Quick take label", "content-egg")}
                            value={attributes.label_quick_take}
                            placeholder={__("Quick take:", "content-egg")}
                            onChange={(val) => setAttributes({ label_quick_take: val })}
                            __nextHasNoMarginBottom
                        />
                    )}
                </PanelBody>
            </InspectorControls>
            <div {...blockProps}>
                {hasData ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/pros-cons"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add items in the sidebar panel.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
